/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore, acceptHMRUpdate } from 'pinia';
import { useGlobalFiltersStore } from '@/stores/global-filters.store';
import { AppsService } from '@/common/apps/apps.service';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import { post as gcsPost } from '@/data/gcs-service.js';
import { runAggregationOnce, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import IdeaPollsCountAggregation from '@/voc/aggregations/idea-polls-count/idea-polls-count.aggregation';
import VocItemListWithSourceIdentifierAggregation from '@/voc/aggregations/voc-item-list-with-source-identifier/voc-item-list-with-source-identifier.aggregation';
import VocItemSuggestionsForSingleItemAggregation from '@/voc/aggregations/voc-item-suggestions-for-single-item/voc-item-suggestions-for-single-item.aggregation';
import VocItemSuggestionsCountForSingleItemAggregation from '@/voc/aggregations/voc-item-suggestions-count-for-single-item/voc-item-suggestions-count-for-single-item.aggregation';
import VocItemAggregation from '@/voc/aggregations/voc-item/voc-item.aggregation';
import VocItemsCountAggregation from '@/voc/aggregations/voc-items-count/voc-items-count.aggregation';
import VocItemsAggregation from '@/voc/aggregations/voc-items/voc-items.aggregation';
import { getUpdateEmail, getVocItemEmailUpdates } from '@/voc/api/emails.api';
import { createCSVBucket, processCSV } from '@/voc/api/import.api';
import {
    addAttachmentToVocItem,
    createAttachmentMedia,
    createVocItemMedia,
    deleteAttachment,
    fetchAttachmentUrl,
    fetchBannerImageUrl,
    updateBannerImageAltText,
} from '@/voc/api/media.api';
import {
    addVote,
    bulkLinkFeedbackToIdea,
    createVocItem,
    deleteVocItem,
    fetchInitialFormData,
    fetchVocItemData,
    linkFeedbackToIdea,
    loadAllVocItems,
    rejectSuggestion,
    revokeVote,
    unlinkFeedbackFromIdea,
    unlinkInsightFromIdea,
    updateVocItem,
} from '@/voc/api/voc-items.api';
import { downloadImage } from '@/voc/helpers/common.helper';
import { EXPLORE_STATUSES, getExploreStatus } from '@/voc/helpers/permissions.helper';
import { useVocItems } from '@/voc/use/use-voc-items.js';
import { UploadQueue } from '@/vuex/modules/voc/helpers/image-upload-queue.helper';
import { useVocLabelsStore } from '@/stores/voc-labels.store';
import { useSavedViewsStore } from '@/stores/saved-views.store';
import { $canSub } from '@pendo/permissions';
import { EventBus } from '@utils/event-bus';
import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import { v4 as uuid } from 'uuid';

import { useSubscriptionStore } from '@/stores/subscription.store';
import { useProductLinesStore } from '@/stores/product-lines.store';
import { useAuthStore } from '@/stores/auth.store';

const { CAPTURE, VALIDATE } = SUB_PRODUCT_AREAS || {};

const ImageUploadQueue = new UploadQueue();

interface BladeState {
    activeItems: Record<string, any>;
    activeItemSuggestedItems: any[];
    activeItemSuggestionsTotal: number | null;
    openVotesTab: boolean;
    openLinkedIdeasTab: boolean;
    goToSuggestions: boolean;
    voteFilter: string | null;
    openingOnTopOfStackedBlade: boolean;
    openingOnTopOfDifferentBlade: boolean;
    leaveStackedBladeOpen: boolean;
    activeItemPromotedRoadmapItems: any[];
    openedFromRoadmap: boolean;
    hasBeenPromotedToCurrentRoadmap: boolean;
    persistOpenItem: boolean;
    selectedCommentId: string | null;
}

interface VocItemsState {
    error: unknown;
    formData: Record<string, any>;
    fetching: boolean;
    blade: BladeState;
    vocItemCount: number | null;
    vocItemCountType: string | null;
    ideaPollCount: number | null;
    activeBannerImage: string;
    activeBannerImageAltText: string;
    attachmentList: any[];
    createDrawerOpen: boolean;
    addVoteModalOpen: boolean;
    bladeChangeTrigger: number;
    map: Record<string, any>;
    loadingLinkedIdeas: boolean;
    loadingLinkedJira: boolean;
    loadingSuggestedIdeas: boolean;
    allIdeas: any[];
    context: string;
    loadingBladeItem: boolean;
    promises: Record<string, Promise<any> | null>;
    activeVisitorVotedIdeasId: string | null;
    activeVisitorVotedIdeasFilters: Record<string, any>;
    visitorVotedIdeas: any[];
    lastVocItemUpdateEmail: Record<string, any>;
    responsesAlreadySentToFeedback: Map<string, string>;
    openNPSBlade: boolean;
    showStatusModalForCurrentSession: boolean;
    isBulkActionInProgress: boolean;
}

export function getInitialState (): VocItemsState {
    return {
        error: null,
        formData: {},
        fetching: false,
        blade: {
            activeItems: {},
            activeItemSuggestedItems: [],
            activeItemSuggestionsTotal: null,
            openVotesTab: false,
            openLinkedIdeasTab: false,
            goToSuggestions: false,
            voteFilter: null,
            openingOnTopOfStackedBlade: false,
            openingOnTopOfDifferentBlade: false,
            leaveStackedBladeOpen: false,
            activeItemPromotedRoadmapItems: [],
            openedFromRoadmap: false,
            hasBeenPromotedToCurrentRoadmap: false,
            persistOpenItem: true,
            selectedCommentId: null,
        },
        vocItemCount: null,
        vocItemCountType: null,
        ideaPollCount: null,
        activeBannerImage: '',
        activeBannerImageAltText: '',
        attachmentList: [],
        createDrawerOpen: false,
        addVoteModalOpen: false,
        bladeChangeTrigger: 0,
        map: {},
        loadingLinkedIdeas: false,
        loadingLinkedJira: false,
        loadingSuggestedIdeas: false,
        allIdeas: [],
        context: '',
        loadingBladeItem: false,
        promises: {},
        activeVisitorVotedIdeasId: null,
        activeVisitorVotedIdeasFilters: {},
        visitorVotedIdeas: [],
        lastVocItemUpdateEmail: {},
        responsesAlreadySentToFeedback: new Map(),
        openNPSBlade: false,
        showStatusModalForCurrentSession: true,
        isBulkActionInProgress: false,
    };
}

export const useVocItemsStore = defineStore('vocItems', {
    state: getInitialState,
    getters: {
        createdById (): string | undefined {
            return this.bladeActiveItem?.createdBy?.id || this.bladeActiveItem?.createdBy?.user?.id;
        },
        canCreateVocItems: () =>
            $canSub()
                .entity('vocItemIdea')
                .action('create'),
        canSubmitFeedback: () =>
            $canSub()
                .entity('vocItemFeedback')
                .action('create'),
        canEditVocItems: () =>
            $canSub()
                .entity('vocItem')
                .action('edit'),
        canEditFeedbackRestricted: () =>
            $canSub()
                .entity('vocItemFeedback')
                .action('edit'),
        canAddVotes: () =>
            $canSub()
                .entity('vote')
                .action('create'),
        canLinkItems: () =>
            $canSub()
                .entity('vocItemAssociation')
                .action('create'),
        canRemoveLinkedIdeas: () =>
            $canSub()
                .entity('vocItemAssociation')
                .action('remove'),
        ownsCurrentVocItem (): boolean {
            const userId = useAuthStore().user.id;
            const { createdById } = this;

            return userId === createdById;
        },
        canEditCurrentVocItem (): boolean {
            return (
                (!!this.canEditFeedbackRestricted &&
                    this.ownsCurrentVocItem &&
                    this.bladeActiveItem?.type === 'feedback') ||
                !!this.canEditVocItems
            );
        },
        list: (state) => values(state.map),
        listByType: (state) => (type = 'idea') => values(state.map).filter((item: any) => item.type === type),
        listCount: (state) => values(state.map).length,
        activeSuggestedItemIds (): string[] {
            if (this.bladeActiveItem?.type === 'idea') {
                return this.blade.activeItemSuggestedItems.map((suggestion: any) => suggestion.rightId);
            }
            if (this.bladeActiveItem?.type === 'feedback') {
                return this.blade.activeItemSuggestedItems.map((suggestion: any) => suggestion.leftId);
            }

            return [];
        },
        hasSuggestionPermissions (): boolean {
            return this.canLinkItems && useSubscriptionStore().getFeatureFlag('enableGoogleDiscoverAI');
        },
        bladeActiveItem: (state) => values(state.blade.activeItems).find((item: any) => item.isVisible),
        bladeHiddenActiveItem: (state) => values(state.blade.activeItems).find((item: any) => !item.isVisible),
        vocItemById: (state) => (id: string) => state.map[id],
        bladeHasMultipleActiveItems: (state) => Object.keys(state.blade.activeItems).length > 1,
        activeItemPromotedRoadmapItems: (state) => cloneDeep(state.blade.activeItemPromotedRoadmapItems),
        appIdsForAggregation: () => (item: any) =>
            flatten(item.appIds.map((appId: string) => AppsService.getAppAggIdsFromOption(appId))),
        listenAppOptions: () => {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');

            return AppsService.sortApps(useAppsStore().onlyIntegratedApps);
        },
        shareUpdateV2Visible (): boolean {
            return (
                this.canEditVocItems &&
                useProductLinesStore().hasProductLinePurchased('voc', 'email') &&
                useSubscriptionStore().getFeatureFlag('vocEmail')
            );
        },
        loadingLinkedJiraIssues: (state) => state.loadingLinkedJira,
    },
    actions: {
        setFetching ({ isFetching }: { isFetching: boolean }) {
            this.fetching = isFetching;
        },
        setFormData ({ type, formData }: { type: string; formData: any }) {
            this.formData[type] = formData;
        },
        setError (error: unknown) {
            this.error = error;
        },
        setBlade ({
            activeItems = {},
            activeItemSuggestedItems = [],
            activeItemSuggestionsTotal = null,
            openVotesTab = false,
            openLinkedIdeasTab = false,
            goToSuggestions = false,
            voteFilter = null,
            openingOnTopOfStackedBlade = false,
            openingOnTopOfDifferentBlade = false,
            leaveStackedBladeOpen = false,
            activeItemPromotedRoadmapItems = [],
            openedFromRoadmap = false,
            hasBeenPromotedToCurrentRoadmap = false,
            persistOpenItem = true,
            selectedCommentId = null,
        }: Partial<BladeState> = {}) {
            this.blade = {
                activeItems,
                activeItemSuggestedItems,
                activeItemSuggestionsTotal,
                openVotesTab,
                openLinkedIdeasTab,
                goToSuggestions,
                voteFilter,
                openingOnTopOfStackedBlade,
                openingOnTopOfDifferentBlade,
                leaveStackedBladeOpen,
                activeItemPromotedRoadmapItems,
                openedFromRoadmap,
                hasBeenPromotedToCurrentRoadmap,
                persistOpenItem,
                selectedCommentId,
            };
        },
        updateAssociationsInMap ({ createdItem }: { createdItem: any }) {
            Object.entries(this.map).forEach(([key, value]: [string, any]) => {
                createdItem.associatedItemIds.forEach((associatedItemId: string) => {
                    if (associatedItemId === key) {
                        if (!value.associatedItemIds) {
                            value.associatedItemIds = [];
                        }
                        if (!value.associatedItemsExpanded) {
                            value.associatedItemsExpanded = [];
                        }
                        value.associatedItemIds.push(createdItem.id);
                        value.associatedItemsExpanded.push(createdItem);
                    }
                });
            });
        },
        setVocItemCount ({ vocItemCount }: { vocItemCount: number | null }) {
            this.vocItemCount = vocItemCount;
        },
        setVocItemCountType ({ type }: { type: string }) {
            this.vocItemCountType = type;
        },
        setIdeaPollCount ({ ideaPollCount }: { ideaPollCount: number }) {
            this.ideaPollCount = ideaPollCount;
        },
        incrementVocItemCount () {
            this.vocItemCount = (this.vocItemCount ?? 0) + 1;
        },
        reduceVocItemCount () {
            this.vocItemCount = (this.vocItemCount ?? 0) - 1;
        },
        setActiveBannerImage ({ bannerImage, altText = '' }: { bannerImage: string; altText?: string }) {
            this.activeBannerImage = bannerImage;
            this.activeBannerImageAltText = altText;
        },
        setActiveBannerImageAltText ({ altText }: { altText: string }) {
            this.activeBannerImageAltText = altText;
        },
        setCreateDrawerOpen (createDrawerOpen: boolean) {
            this.createDrawerOpen = createDrawerOpen;
        },
        setAddVoteModalOpen (addVoteModalOpen: boolean) {
            this.addVoteModalOpen = addVoteModalOpen;
        },
        incrementBladeChangeTrigger () {
            this.bladeChangeTrigger++;
        },
        setMap ({ map }: { map: Record<string, any> }) {
            this.map = map;
        },
        pushToAttachmentList ({ fileData }: { fileData: any }) {
            this.attachmentList.push(fileData);
        },
        updateAttachmentList ({ fileData }: { fileData: any }) {
            this.attachmentList = this.attachmentList.map((attachment: any) => {
                if (attachment.id === fileData.id) {
                    attachment = Object.assign({}, attachment, fileData);

                    if (fileData.newId) {
                        attachment.id = fileData.newId;
                    }
                }

                return attachment;
            });
        },
        removeAttachmentFromList ({ id }: { id: string }) {
            this.attachmentList = this.attachmentList.filter((fileData: any) => fileData.id !== id);
        },
        resetAttachmentList () {
            this.attachmentList = [];
        },
        setLoadingLinkedIdeas ({ isLoading }: { isLoading: boolean }) {
            this.loadingLinkedIdeas = isLoading;
        },
        setLoadingLinkedJira ({ isLoading }: { isLoading: boolean }) {
            this.loadingLinkedJira = isLoading;
        },
        setActiveSuggestions ({ suggestions }: { suggestions: any[] }) {
            this.blade.activeItemSuggestedItems = suggestions;
            if (this.context === 'feedback' && this.blade.activeItems.feedback) {
                this.blade.activeItems.feedback.numOfSuggestions = suggestions.length;
            }
        },
        setSuggestionsTotal ({ total }: { total: number }) {
            this.blade.activeItemSuggestionsTotal = total;
        },
        decrementSuggestionsTotal () {
            if ((this.blade.activeItemSuggestionsTotal ?? 0) > 0) {
                this.blade.activeItemSuggestionsTotal! -= 1;
            }
        },
        setLoadingSuggestions ({ loading }: { loading: boolean }) {
            this.loadingSuggestedIdeas = loading;
        },
        setGoToSuggestions ({ goToSuggestions }: { goToSuggestions: boolean }) {
            this.blade.goToSuggestions = goToSuggestions;
        },
        setAllIdeas ({ vocItems }: { vocItems: any[] }) {
            this.allIdeas = vocItems;
        },
        setBladeActiveItems ({ activeItems }: { activeItems: Record<string, any> }) {
            this.blade.activeItems = activeItems;
        },
        setContext ({ context }: { context: string }) {
            this.context = context;
        },
        setLoadingBladeItem ({ loading }: { loading: boolean }) {
            this.loadingBladeItem = loading;
        },
        updateBladeHiddenVocItemLinkedItems ({ linkedItem, lastUpdatedAt }: { linkedItem: any; lastUpdatedAt: any }) {
            const hiddenItem = values(this.blade.activeItems).find((item: any) => !item.isVisible);
            const associatedItemIds = hiddenItem.associatedItemIds
                ? [...hiddenItem.associatedItemIds, linkedItem.id]
                : [linkedItem.id];
            const associatedItemsExpanded = hiddenItem.associatedItemsExpanded
                ? [...hiddenItem.associatedItemsExpanded, linkedItem]
                : [linkedItem];
            const updatedItem = Object.assign(hiddenItem, {
                associatedItemIds,
                associatedItemsExpanded,
                lastUpdatedAt,
            });
            this.blade.activeItems = {
                ...this.blade.activeItems,
                [updatedItem.type]: updatedItem,
            };
        },
        updateLinkedItemsInMap ({ id, linkedItem, lastUpdatedAt }: { id: string; linkedItem: any; lastUpdatedAt: any }) {
            this.map[id].associatedItemIds = this.map[id].associatedItemIds
                ? [...this.map[id].associatedItemIds, linkedItem.id]
                : [linkedItem.id];
            this.map[id].associatedItemsExpanded = this.map[id].associatedItemsExpanded
                ? [...this.map[id].associatedItemsExpanded, linkedItem]
                : [linkedItem];
            this.map[id].lastUpdatedAt = lastUpdatedAt;
        },
        setActiveItemPromotedRoadmapItems (activeItemPromotedRoadmapItems: any[]) {
            this.blade.activeItemPromotedRoadmapItems = activeItemPromotedRoadmapItems;
        },
        setPromise ({ promise, key }: { promise: Promise<any> | null; key: string }) {
            this.promises = { ...this.promises, [key]: promise };
        },
        clearVisitorVotedIdeas () {
            this.activeVisitorVotedIdeasId = null;
            this.activeVisitorVotedIdeasFilters = {};
            this.visitorVotedIdeas = [];
            this.setVocItems({ vocItems: [], type: 'idea' });
        },
        updateVisitorVotedIdeas ({ vocItem }: { vocItem: any }) {
            if (!this.activeVisitorVotedIdeasId) return;

            if (this.visitorVotedIdeas.some((idea: any) => idea.id === vocItem.id)) {
                this.visitorVotedIdeas = this.visitorVotedIdeas.map((idea: any) =>
                    (idea.id === vocItem.id ? vocItem : idea),
                );
            }
        },
        removeVisitorVotedIdea ({ vocItemId }: { vocItemId: string }) {
            this.visitorVotedIdeas = this.visitorVotedIdeas.filter((idea: any) => idea.id !== vocItemId);
        },
        updateItem ({ item }: { item: any }) {
            this.map[item.id] = item;
            this.updateVisitorVotedIdeas({ vocItem: item });
            if (this.blade.activeItems[item.type]?.id === item.id) {
                this.blade.activeItems = {
                    ...this.blade.activeItems,
                    [item.type]: item,
                };
            }
        },
        setLastVocItemUpdateEmail (lastVocItemUpdateEmail: any) {
            this.lastVocItemUpdateEmail = lastVocItemUpdateEmail;
        },
        addResponseToResponsesAlreadySentToFeedback ({ sourceIdentifier, vocItemId }: { sourceIdentifier: string; vocItemId: string }) {
            this.responsesAlreadySentToFeedback.set(sourceIdentifier, vocItemId);
            this.responsesAlreadySentToFeedback = new Map(this.responsesAlreadySentToFeedback);
        },
        removeResponseFromResponsesAlreadySentToFeedback ({ sourceIdentifier }: { sourceIdentifier: string }) {
            this.responsesAlreadySentToFeedback.delete(sourceIdentifier);
            this.responsesAlreadySentToFeedback = new Map(this.responsesAlreadySentToFeedback);
        },
        setOpenNPSBlade (value: boolean) {
            this.openNPSBlade = value;
        },
        setShowStatusModalForCurrentSession () {
            this.showStatusModalForCurrentSession = !this.showStatusModalForCurrentSession;
        },
        setIsBulkActionInProgress (value: boolean) {
            this.isBulkActionInProgress = value;
        },
        fetchVocItems (
            { type = 'idea', filters, revenue = false, store = true }: { type?: string; filters: any; revenue?: boolean; store?: boolean },
        ) {
            if (this.promises.vocItems) {
                return this.promises.vocItems;
            }

            const promise = (async () => {
                if (revenue) {
                    await useSubscriptionStore().loadSetting({ setting: 'revenue' }).catch(
                        (error: any) => error,
                    );
                }

                const revenueField = revenue ? get(useSubscriptionStore().settings, 'revenue.value', null) : null;
                let accountTypeField = get(useSubscriptionStore().settings, 'accountType.value', null);
                if (!accountTypeField) {
                    await useSubscriptionStore().loadSetting({ setting: 'accountType' }).catch(
                        (error: any) => error,
                    );
                    accountTypeField = get(useSubscriptionStore().settings, 'accountType.value', null);
                }

                const vocItems: any = await runAggregationOnce(VocItemsAggregation, {
                    ...filters,
                    revenueField,
                    accountTypeField,
                    type,
                });

                if (store) {
                    this.setVocItems({ vocItems, type });
                }

                return vocItems;
            })().finally(() => {
                this.setPromise({ key: 'vocItems', promise: null });
            });

            this.setPromise({ key: 'vocItems', promise });

            return promise;
        },
        fetchIdeasVotedByVisitor (
            {
                visitorId,
                store = true,
                reFetch = false,
                filters = {},
            }: { visitorId: string; store?: boolean; reFetch?: boolean; filters?: Record<string, any> },
        ) {
            const filtersKey = JSON.stringify(filters ?? {});
            const promiseKey = `visitorVotedIdeas-${visitorId}-${filtersKey}`;

            if (store && this.activeVisitorVotedIdeasId !== visitorId) {
                this.visitorVotedIdeas = [];
                this.activeVisitorVotedIdeasFilters = {};
            }

            if (this.promises[promiseKey]) {
                return this.promises[promiseKey];
            }

            const hasCachedIdeasForVisitorAndFilters =
                this.activeVisitorVotedIdeasId === visitorId &&
                JSON.stringify(this.activeVisitorVotedIdeasFilters ?? {}) === filtersKey &&
                !isEmpty(this.visitorVotedIdeas);
            const shouldUseCachedIdeas = store && !reFetch && hasCachedIdeasForVisitorAndFilters;

            if (shouldUseCachedIdeas) {
                return Promise.resolve(this.visitorVotedIdeas);
            }

            if (store) {
                this.activeVisitorVotedIdeasId = visitorId;
                this.activeVisitorVotedIdeasFilters = filters ?? {};
            }

            const isStillActiveRequest = () =>
                this.activeVisitorVotedIdeasId === visitorId &&
                JSON.stringify(this.activeVisitorVotedIdeasFilters ?? {}) === filtersKey;

            const promise = (async () => {
                try {
                    const votedIdeas: any = await runAggregationOnce(VocItemsAggregation, {
                        ...filters,
                        type: 'idea',
                        votedByVisitorId: visitorId,
                    });

                    const shouldStoreResponse = store && isStillActiveRequest();

                    if (shouldStoreResponse) {
                        this.visitorVotedIdeas = votedIdeas;
                        this.setVocItems({ vocItems: votedIdeas, type: 'idea' });
                    }

                    return votedIdeas;
                } catch (error) {
                    const shouldSurfaceError = !store || isStillActiveRequest();

                    if (shouldSurfaceError) {
                        this.setError(error);
                    }
                    throw error;
                }
            })().finally(() => {
                this.setPromise({ key: promiseKey, promise: null });
            });

            this.setPromise({ key: promiseKey, promise });

            return promise;
        },
        async openBladeForItem (
            {
                vocItemId,
                roadmapItemId,
                openVotesTab = false,
                openLinkedIdeasTab = false,
                goToSuggestions = false,
                voteFilter = null,
                openingOnTopOfStackedBlade = false,
                openingOnTopOfDifferentBlade = false,
                openedFromRoadmap = false,
                hasBeenPromotedToCurrentRoadmap = false,
                persistOpenItem = true,
                selectedCommentId = null,
                type = 'idea',
                isExploreSetup = false,
            }: {
                vocItemId: string;
                roadmapItemId?: string;
                openVotesTab?: boolean;
                openLinkedIdeasTab?: boolean;
                goToSuggestions?: boolean;
                voteFilter?: string | null;
                openingOnTopOfStackedBlade?: boolean;
                openingOnTopOfDifferentBlade?: boolean;
                openedFromRoadmap?: boolean;
                hasBeenPromotedToCurrentRoadmap?: boolean;
                persistOpenItem?: boolean;
                selectedCommentId?: string | null;
                type?: string;
                isExploreSetup?: boolean;
            },
        ) {
            const { addVocItem, getVocItemById, cleanupVocItems } = useVocItems({ type });
            try {
                let addToMap = true;
                let newItem: any;
                const vocItemFromStore = this.map[vocItemId] || getVocItemById(vocItemId);
                if (!vocItemFromStore) {
                    this.setLoadingBladeItem({ loading: true });
                    const revenueField = get(useSubscriptionStore().settings, 'revenue.value', null);
                    const accountDisplayName = get(useSubscriptionStore().settings, 'accountDisplayName.value', null);
                    (VocItemAggregation as any).subProductArea = type === 'idea' ? VALIDATE : CAPTURE;
                    if (this.promises[`vocItem-${vocItemId}`]) {
                        newItem = await this.promises[`vocItem-${vocItemId}`];
                    } else {
                        const promise = runAggregationOnce(VocItemAggregation, {
                            vocItemId,
                            type,
                            segmentId: type === 'idea' ? useGlobalFiltersStore().selectedSegmentId : null,
                            allTime: type === 'idea' ? useGlobalFiltersStore().allTime : true,
                            timeSeriesConfig: type === 'idea' ? useGlobalFiltersStore().timeSeriesSpec : null,
                            revenueField,
                            accountDisplayName,
                            expandInsights: isExploreSetup,
                        })
                            .then(async (item: any) => {
                                if (isEmpty(item)) {
                                    addToMap = false;

                                    return runAggregationOnce(VocItemAggregation, {
                                        vocItemId,
                                        type,
                                        segmentId: null,
                                        allTime: true,
                                        timeSeriesConfig: null,
                                        revenueField,
                                        accountDisplayName,
                                        expandInsights: isExploreSetup,
                                    });
                                }

                                return item;
                            })
                            .finally(() => this.setPromise({ key: `vocItem-${vocItemId}`, promise: null }));

                        this.setPromise({ key: `vocItem-${vocItemId}`, promise });
                        newItem = await promise;
                    }
                    if (addToMap && type === 'idea') {
                        this.setMap({
                            map: {
                                ...this.map,
                                [vocItemId]: newItem,
                            },
                        });
                    } else if (addToMap) {
                        addVocItem(newItem);
                    }
                }

                const resolvedVocItem = this.map[vocItemId] || getVocItemById(vocItemId) || newItem;
                if (isEmpty(resolvedVocItem)) {
                    throw new Error('VocItem not found');
                }

                const vocItem = {
                    ...resolvedVocItem,
                    roadmapItemId,
                    isVisible: true,
                };
                const activeItems = keyBy(
                    values(this.blade.activeItems).map((item: any) => {
                        return {
                            ...item,
                            isVisible: false,
                        };
                    }),
                    'type',
                );

                this.setBlade({
                    activeItems: {
                        ...activeItems,
                        [vocItem.type]: vocItem,
                    },
                    openVotesTab,
                    openLinkedIdeasTab,
                    goToSuggestions,
                    voteFilter,
                    openingOnTopOfStackedBlade,
                    openingOnTopOfDifferentBlade,
                    openedFromRoadmap,
                    hasBeenPromotedToCurrentRoadmap,
                    persistOpenItem,
                    selectedCommentId,
                });

                const ideaBlade = hasSegmentFlag('suggestedFeedbackImprovements') && vocItem.type === 'idea';
                if (this.hasSuggestionPermissions && !ideaBlade) {
                    this.setSuggestedItems({ item: vocItem });
                }

                return vocItem;
            } catch (error) {
                this.setError(error);

                throw error;
            } finally {
                this.setLoadingBladeItem({ loading: false });
                cleanupVocItems();
            }
        },
        closeBlade ({ leaveStackedBladeOpen = false, closeAll = false }: { leaveStackedBladeOpen?: boolean; closeAll?: boolean } = {}) {
            if (closeAll) {
                this.setBlade({
                    leaveStackedBladeOpen,
                });

                return;
            }

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [this.bladeActiveItem?.type]: _activeItem, ...hiddenActiveItem } = this.blade.activeItems;

            let newActiveItem: any = {};
            if (!isEmpty(hiddenActiveItem)) {
                newActiveItem = {
                    ...values(hiddenActiveItem)[0],
                    isVisible: true,
                };
            }

            this.setBlade({
                activeItems: {
                    ...(!isEmpty(newActiveItem) && { [newActiveItem.type]: newActiveItem }),
                },
                openLinkedIdeasTab: !isEmpty(newActiveItem),
                leaveStackedBladeOpen,
            });

            if (newActiveItem.type === 'feedback' && newActiveItem.numOfSuggestions > 0) {
                this.setSuggestedItems({ item: newActiveItem });
            }
        },
        loadOne () {
            return this.bladeActiveItem;
        },
        async loadInitialFormData ({ reFetch = false, type = 'idea' }: { reFetch?: boolean; type?: string } = {}) {
            if (!isEmpty(this.formData[type]) && !reFetch) {
                return;
            }

            try {
                this.setFetching({ isFetching: true });
                const formData = await fetchInitialFormData(type);
                this.setFormData({ type, formData });
            } catch (error) {
                this.setError(error);
                throw error;
            } finally {
                this.setFetching({ isFetching: false });
            }
        },
        async createVocItem (
            { vocItem, acceptedSuggestions = [], loadBlade = true, storeInMap = true }: { vocItem: any; acceptedSuggestions?: string[]; loadBlade?: boolean; storeInMap?: boolean },
        ) {
            try {
                let createdItem = await createVocItem(vocItem);
                createdItem = {
                    ...createdItem,
                    vocItemTitle: vocItem.formData[0]?.content,
                    vocItemDescription: vocItem.formData[1]?.content,
                    kind: 'vocItem',
                };
                if (createdItem.type === 'idea') {
                    createdItem = {
                        ...createdItem,
                        voteCount: 0,
                        accountVoteCount: 0,
                        createdByName: createdItem.createdBy.user.username,
                    };
                    this.incrementVocItemCount();
                    this.loadAll({ reFetch: true });
                }

                const revenueField = get(useSubscriptionStore().settings, 'revenue.value', null);
                const accountDisplayName = get(useSubscriptionStore().settings, 'accountDisplayName.value', null);
                (VocItemAggregation as any).subProductArea = createdItem.type === 'idea' ? VALIDATE : CAPTURE;
                const itemFromAgg = await runAggregationOnce(VocItemAggregation, {
                    vocItemId: createdItem.id,
                    appId: this.appIdsForAggregation(createdItem),
                    type: createdItem.type,
                    segmentId: createdItem.type === 'idea' ? useGlobalFiltersStore().selectedSegmentId : null,
                    allTime: createdItem.type === 'idea' ? useGlobalFiltersStore().allTime : true,
                    timeSeriesConfig: createdItem.type === 'idea' ? useGlobalFiltersStore().timeSeriesSpec : null,
                    shouldFilterVotes: false,
                    revenueField,
                    accountDisplayName,
                }) as any;
                const formattedItem = { ...createdItem, ...itemFromAgg };

                if (formattedItem.labelIds?.length > 0) {
                    await useVocLabelsStore().loadLabels({ reFetch: true });
                }

                if (vocItem.associatedItemIds?.length > 0) {
                    vocItem.associatedItemIds.forEach((assocId: string) => {
                        window.pendo?.track('feedback-linked-to-idea', {
                            suggested: acceptedSuggestions.includes(assocId),
                            context: `${formattedItem.type}-form`,
                        });
                    });

                    this.updateAssociationsInMap({ createdItem: formattedItem });
                }
                if (storeInMap && createdItem.type === 'idea') {
                    this.setMap({
                        map: {
                            ...this.map,
                            [formattedItem.id]: formattedItem,
                        },
                    });
                } else if (storeInMap) {
                    const { addVocItem, cleanupVocItems } = useVocItems({ type: createdItem.type });
                    addVocItem(formattedItem);
                    cleanupVocItems();
                }

                if (formattedItem.type === 'idea' && loadBlade) {
                    this.openBladeForItem({
                        vocItemId: formattedItem.id,
                    });
                }

                return formattedItem;
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async updateVocItem ({ vocItemId, changes }: { vocItemId: string; changes: any }) {
            try {
                const updatedItem = await updateVocItem(vocItemId, changes);
                const username = updatedItem.lastUpdatedBy?.user?.username;

                const { getVocItemById, updateVocItem: updateVocItemState, cleanupVocItems } = useVocItems({
                    type: updatedItem.type,
                });

                const existingItem =
                    this.blade.activeItems[updatedItem.type]?.id === updatedItem.id
                        ? this.blade.activeItems[updatedItem.type]
                        : this.map[updatedItem.id] || getVocItemById(updatedItem.id);
                const formattedUpdatedItem = {
                    ...existingItem,
                    ...updatedItem,
                    createdBy: existingItem?.createdBy ?? updatedItem?.createdBy,
                    ownedByUserId: updatedItem?.ownedByUser?.id,
                    lastUpdatedBy: {
                        username,
                    },
                    onBehalfOf: existingItem?.onBehalfOf,
                    ...(updatedItem.formData && {
                        vocItemTitle: updatedItem.formData[0]?.content,
                        vocItemDescription: updatedItem.formData[1]?.content,
                    }),
                    ...(this.bladeActiveItem?.type === 'idea' && {
                        workaroundObj: {
                            ...updatedItem.workaround,
                            lastSentAt: updatedItem.workaroundNotificationLastSentAt,
                            lastSentByUser: updatedItem.workaroundNotificationLastSentByUser,
                        },
                    }),
                    ...(changes.highlight && {
                        highlight: {
                            ...existingItem?.highlight,
                            ...changes.highlight,
                        },
                    }),
                };

                if (changes.labels) await useVocLabelsStore().loadLabels({ reFetch: true });

                delete formattedUpdatedItem.workaround;
                delete formattedUpdatedItem.workaroundNotificationLastSentAt;
                delete formattedUpdatedItem.workaroundNotificationLastSentByUser;

                if (this.map[updatedItem.id]) {
                    this.setMap({
                        map: {
                            ...this.map,
                            [updatedItem.id]: formattedUpdatedItem,
                        },
                    });
                } else if (getVocItemById(updatedItem.id)) {
                    updateVocItemState(updatedItem.id, formattedUpdatedItem);
                }
                this.updateVisitorVotedIdeas({ vocItem: formattedUpdatedItem });
                cleanupVocItems();

                if (this.blade.activeItems[updatedItem.type]?.id === updatedItem.id) {
                    this.setBladeActiveItems({
                        activeItems: {
                            ...this.blade.activeItems,
                            [updatedItem.type]: formattedUpdatedItem,
                        },
                    });
                }

                if (changes.formData && this.blade.activeItems.feedback?.associatedItemIds?.includes(updatedItem.id)) {
                    const associatedItemsExpanded = this.blade.activeItems.feedback.associatedItemsExpanded?.map(
                        (item: any) => {
                            if (item.id === updatedItem.id) {
                                return {
                                    ...item,
                                    ...formattedUpdatedItem,
                                };
                            }

                            return item;
                        },
                    );
                    const updatedFeedbackItem = {
                        ...this.blade.activeItems.feedback,
                        associatedItemsExpanded,
                    };

                    this.setBladeActiveItems({
                        activeItems: {
                            ...this.blade.activeItems,
                            feedback: updatedFeedbackItem,
                        },
                    });
                    if (this.map[updatedItem.id]) {
                        this.setMap({
                            map: {
                                ...this.map,
                                [this.blade.activeItems.feedback.id]: updatedFeedbackItem,
                            },
                        });
                    } else {
                        const { getVocItemById: getVocItemByIdInner, updateVocItem: updateVocItemInner, cleanupVocItems: cleanupVocItemsInner } = useVocItems({
                            type: updatedFeedbackItem.type,
                        });
                        if (getVocItemByIdInner(this.blade.activeItems.feedback.id)) {
                            updateVocItemInner(this.blade.activeItems.feedback.id, updatedFeedbackItem);
                        }
                        cleanupVocItemsInner();
                    }
                }

                this.incrementBladeChangeTrigger();
                if (this.blade.openingOnTopOfStackedBlade) {
                    EventBus.$emit('update-stacked-blade', formattedUpdatedItem);
                }

                return formattedUpdatedItem;
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async loadVocItemCount ({ reFetch = false, type = 'idea' }: { reFetch?: boolean; type?: string } = {}) {
            if (type === this.vocItemCountType && !isNull(this.vocItemCount) && !reFetch) {
                return;
            }

            try {
                this.setFetching({ isFetching: true });
                this.setVocItemCount({ vocItemCount: null });

                (VocItemsCountAggregation as any).subProductArea = type === 'idea' ? VALIDATE : CAPTURE;
                const vocItemCount = (await runAggregationOnce(VocItemsCountAggregation, { type }) as any).count;

                this.setVocItemCount({ vocItemCount });
                this.setVocItemCountType({ type });
            } catch (error) {
                this.setError(error);
                throw error;
            } finally {
                this.setFetching({ isFetching: false });
            }
        },
        async revokeVoteFromVocItem ({ vocItemId, voteId }: { vocItemId: string; voteId: string }) {
            try {
                await revokeVote(vocItemId, voteId);

                const currentItem = this.map[vocItemId] || this.bladeActiveItem;
                const revenueField = get(useSubscriptionStore().settings, 'revenue.value', null);
                const accountDisplayName = get(useSubscriptionStore().settings, 'accountDisplayName.value', null);
                (VocItemAggregation as any).subProductArea = currentItem.type === 'idea' ? VALIDATE : CAPTURE;
                const updatedItem = await runAggregationOnce(VocItemAggregation, {
                    vocItemId,
                    appId: this.appIdsForAggregation(currentItem),
                    type: currentItem.type,
                    segmentId: currentItem.type === 'idea' ? useGlobalFiltersStore().selectedSegmentId : null,
                    allTime: currentItem.type === 'idea' ? useGlobalFiltersStore().allTime : true,
                    timeSeriesConfig: currentItem.type === 'idea' ? useGlobalFiltersStore().timeSeriesSpec : null,
                    shouldFilterVotes: false,
                    revenueField,
                    accountDisplayName,
                }) as any;

                if (this.map[vocItemId]) {
                    this.setMap({
                        map: {
                            ...this.map,
                            [updatedItem.id]: {
                                ...currentItem,
                                ...updatedItem,
                            },
                        },
                    });
                } else {
                    const { getVocItemById, updateVocItem: updateVocItemState, cleanupVocItems } = useVocItems({ type: currentItem.type });
                    if (getVocItemById(vocItemId)) {
                        updateVocItemState(vocItemId, {
                            ...currentItem,
                            ...updatedItem,
                        });
                    }
                    cleanupVocItems();
                }
                if (this.blade.activeItems[updatedItem.type]?.id === updatedItem.id) {
                    this.setBladeActiveItems({
                        activeItems: {
                            ...this.blade.activeItems,
                            [updatedItem.type]: {
                                ...this.blade.activeItems[updatedItem.type],
                                ...updatedItem,
                            },
                        },
                    });
                }

                if (this.activeVisitorVotedIdeasId) {
                    await this.fetchIdeasVotedByVisitor({
                        visitorId: this.activeVisitorVotedIdeasId,
                        reFetch: true,
                        filters: this.activeVisitorVotedIdeasFilters,
                    });
                }

                this.incrementBladeChangeTrigger();
                if (this.blade.openingOnTopOfStackedBlade) {
                    EventBus.$emit('update-stacked-blade', {
                        ...currentItem,
                        ...updatedItem,
                    });
                }
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        updateBladeActiveVocItem ({ changes }: { changes: any }) {
            if (!this.bladeActiveItem) return;
            const activeItem = values(this.blade.activeItems).find((item: any) => item.isVisible);
            const updatedItem = Object.assign(activeItem, changes);
            this.blade.activeItems = {
                ...this.blade.activeItems,
                [updatedItem.type]: updatedItem,
            };
            if (this.map[this.bladeActiveItem.id]) {
                this.setMap({
                    map: {
                        ...this.map,
                        [this.bladeActiveItem.id]: this.bladeActiveItem,
                    },
                });
            } else {
                const { getVocItemById, updateVocItem: updateVocItemState, cleanupVocItems } = useVocItems({
                    type: this.bladeActiveItem.type,
                });
                if (getVocItemById(this.bladeActiveItem.id)) {
                    updateVocItemState(this.bladeActiveItem.id, this.bladeActiveItem);
                }
                cleanupVocItems();
            }

            if (values(changes).length === 1 && changes.numOfSuggestions) return;

            this.incrementBladeChangeTrigger();
        },
        async addVote (
            { vocItemId, vocItem, type = 'idea', onBehalfOfId, onBehalfOfType, strength, comment, accountId }: {
                vocItemId: string;
                vocItem?: any;
                type?: string;
                onBehalfOfId?: string;
                onBehalfOfType?: string;
                strength?: number;
                comment?: string;
                accountId?: string;
            },
        ) {
            const { getVocItemById, updateVocItem: updateVocItemState, cleanupVocItems } = useVocItems({ type });
            try {
                const voteInfo = await addVote(vocItemId, onBehalfOfId, onBehalfOfType, strength, comment, accountId);

                const currentItem =
                    vocItem || this.map[vocItemId] || getVocItemById(vocItemId) || this.bladeActiveItem;
                const revenueField = get(useSubscriptionStore().settings, 'revenue.value', null);
                const accountDisplayName = get(useSubscriptionStore().settings, 'accountDisplayName.value', null);
                (VocItemAggregation as any).subProductArea = currentItem.type === 'idea' ? VALIDATE : CAPTURE;
                const updatedItem = await runAggregationOnce(VocItemAggregation, {
                    vocItemId,
                    appId: this.appIdsForAggregation(currentItem),
                    type: currentItem.type,
                    segmentId: currentItem.type === 'idea' ? useGlobalFiltersStore().selectedSegmentId : null,
                    allTime: currentItem.type === 'idea' ? useGlobalFiltersStore().allTime : true,
                    timeSeriesConfig: currentItem.type === 'idea' ? useGlobalFiltersStore().timeSeriesSpec : null,
                    shouldFilterVotes: false,
                    revenueField,
                    accountDisplayName,
                }) as any;
                updatedItem.associatedItemsExpanded = currentItem.associatedItemsExpanded;
                const mergedUpdatedItem = {
                    ...currentItem,
                    ...updatedItem,
                };
                if (this.map[vocItemId]) {
                    this.setMap({
                        map: {
                            ...this.map,
                            [updatedItem.id]: mergedUpdatedItem,
                        },
                    });
                } else if (getVocItemById(vocItemId)) {
                    updateVocItemState(vocItemId, mergedUpdatedItem);
                }
                this.updateVisitorVotedIdeas({ vocItem: mergedUpdatedItem });
                if (this.blade.activeItems[updatedItem.type]?.id === updatedItem.id) {
                    this.setBladeActiveItems({
                        activeItems: {
                            ...this.blade.activeItems,
                            [updatedItem.type]: {
                                ...this.blade.activeItems[updatedItem.type],
                                ...updatedItem,
                            },
                        },
                    });
                }

                this.incrementBladeChangeTrigger();
                if (this.blade.openingOnTopOfStackedBlade) {
                    EventBus.$emit('update-stacked-blade', {
                        ...currentItem,
                        ...updatedItem,
                    });
                }

                return voteInfo;
            } catch (error) {
                this.setError(error);
                throw error;
            } finally {
                cleanupVocItems();
            }
        },
        async createAttachmentMedia ({ imageFiles, vocItemId }: { imageFiles: any[]; vocItemId: string }) {
            const failedUploadedFiles: any[] = [];
            for (const imageFile of imageFiles) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    await this.createAndAttachImage({ imageFile, vocItemId });
                } catch (err) {
                    failedUploadedFiles.push(imageFile);
                    this.setError(err);
                }
            }

            return failedUploadedFiles;
        },
        async createAndAttachImage ({ imageFile, vocItemId }: { imageFile: any; vocItemId: string }) {
            const attachmentResp = await createAttachmentMedia(vocItemId, imageFile.name, imageFile.type);
            await gcsPost(attachmentResp.uploadUrl, imageFile, attachmentResp.formData);
            const vocItem = await addAttachmentToVocItem(vocItemId, attachmentResp.mediaFileId);

            return { id: attachmentResp.mediaFileId, vocItem };
        },
        async attachLinksToVocItem ({ linkFiles, vocItemId }: { linkFiles: any[]; vocItemId: string }) {
            const linksData = linkFiles.map((linkFile: any) => {
                return { linkUrl: linkFile.name, title: linkFile.title };
            });
            await this.updateVocItem({ vocItemId, changes: { linksData } });
        },
        async createVocItemMedia ({ imageFile, altText }: { imageFile: any; altText: string }) {
            try {
                const vocItemId = this.bladeActiveItem.id;
                const bannerImageResp = await createVocItemMedia(vocItemId, 'bannerImage', { altText, imageFile });
                const addBannerImageToVocItemResp = await updateVocItem(vocItemId, {
                    bannerImageId: bannerImageResp.mediaFileId,
                });

                this.updateBladeActiveVocItem({
                    changes: {
                        bannerImageId: bannerImageResp.mediaFileId,
                        lastUpdatedAt: addBannerImageToVocItemResp.lastUpdatedAt,
                        lastUpdatedBy: {
                            username: addBannerImageToVocItemResp.lastUpdatedBy.user.username,
                        },
                    },
                });
                const vocItemBannerImageId = this.bladeActiveItem.bannerImageId;
                const bannerImageData = await fetchBannerImageUrl(vocItemId, vocItemBannerImageId);

                this.setActiveBannerImage({ bannerImage: bannerImageData.url, altText: bannerImageData.altText || '' });
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async fetchBannerImage () {
            try {
                const vocItemBannerImageId = this.bladeActiveItem.bannerImageId;
                const vocItemId = this.bladeActiveItem.id;

                if (vocItemBannerImageId !== '') {
                    const bannerImageResp = await fetchBannerImageUrl(vocItemId, vocItemBannerImageId);
                    this.setActiveBannerImage({ bannerImage: bannerImageResp.url, altText: bannerImageResp.altText || '' });
                } else {
                    this.setActiveBannerImage({ bannerImage: '', altText: '' });
                }
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async removeBannerImage () {
            try {
                const vocItemId = this.bladeActiveItem.id;
                const addBannerImageToVocItemResp = await updateVocItem(vocItemId, { bannerImageId: '' });
                this.updateBladeActiveVocItem({
                    changes: {
                        bannerImageId: '',
                        lastUpdatedAt: addBannerImageToVocItemResp.lastUpdatedAt,
                        lastUpdatedBy: {
                            username: addBannerImageToVocItemResp.lastUpdatedBy.user.username,
                        },
                    },
                });
                this.setActiveBannerImage({ bannerImage: '', altText: '' });
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async updateBannerImageAltText ({ altText }: { altText: string }) {
            try {
                const vocItemId = this.bladeActiveItem.id;
                const { bannerImageId } = this.bladeActiveItem;
                await updateBannerImageAltText(vocItemId, bannerImageId, altText);
                this.setActiveBannerImageAltText({ altText });
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async loadIdeaPollCount ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (!isNull(this.ideaPollCount) && !reFetch) {
                return;
            }
            try {
                this.setFetching({ isFetching: true });
                const { count } = await runAggregationOnce(IdeaPollsCountAggregation, {}) as any;
                this.setIdeaPollCount({ ideaPollCount: count });
            } catch (error) {
                this.setError(error);
                throw error;
            } finally {
                this.setFetching({ isFetching: false });
            }
        },
        loadAttachmentsData ({ vocItem }: { vocItem: any }) {
            this.resetAttachmentList();

            const attachmentCount = !!vocItem.attachmentIds?.length;
            if (attachmentCount) {
                vocItem.attachmentIds.forEach((attachmentId: string) => {
                    this.pushToAttachmentList({
                        fileData: {
                            id: attachmentId,
                            type: 'image',
                            loading: true,
                        },
                    });

                    fetchAttachmentUrl(vocItem.id, attachmentId).then((attachment: any) => {
                        downloadImage({
                            url: attachment.url,
                            type: attachment.fileType,
                            name: attachment.fileName,
                        }).then((file: any) => {
                            this.updateAttachmentList({
                                fileData: {
                                    file,
                                    id: attachmentId,
                                    type: 'image',
                                    loading: false,
                                },
                            });
                        }).catch(() => {
                            this.updateAttachmentList({ fileData: { id: attachmentId, loading: false } });
                        });
                    }).catch(() => {
                        this.updateAttachmentList({ fileData: { id: attachmentId, loading: false } });
                    });
                });
            }

            const linkCount = !!vocItem.linksData?.length;
            if (linkCount) {
                vocItem.linksData.forEach((linkData: any) => {
                    this.pushToAttachmentList({
                        fileData: {
                            file: {
                                name: linkData.linkUrl,
                                title: linkData.title,
                            },
                            type: 'link',
                            loading: false,
                            id: uuid(),
                        },
                    });
                });
            }
        },
        async removeVocItem ({ vocItemId, type = 'idea' }: { vocItemId: string; type?: string }) {
            try {
                await deleteVocItem(vocItemId);
                this.loadAll({ reFetch: true });
                this.reduceVocItemCount();
                this.incrementBladeChangeTrigger();

                const { getVocItemById, removeVocItem: removeVocItemState, updateVocItem: updateVocItemState, cleanupVocItems } = useVocItems({ type });

                const vocItem = this.map[vocItemId] || getVocItemById(vocItemId);
                const vocItemType = vocItem?.type || this.bladeActiveItem?.type || type;

                if (this.map[vocItemId]) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { [vocItemId]: _item, ...newMap } = this.map;
                    this.setMap({ map: newMap });
                } else if (vocItem) {
                    removeVocItemState(vocItemId);
                }
                this.removeVisitorVotedIdea({ vocItemId });

                if (vocItem?.labelIds?.length > 0) {
                    await useVocLabelsStore().loadLabels({ reFetch: true });
                }

                const savedViewsStore = useSavedViewsStore();
                if (vocItemType === 'feedback' && savedViewsStore.activeSavedView?.id) {
                    savedViewsStore.updateItemCount({
                        count: this.listByType('feedback').length,
                        type: 'feedback',
                        id: savedViewsStore.activeSavedView.id as string,
                    });
                }

                const typeOfActiveItem = Object.keys(this.blade.activeItems).find(
                    (key) => this.blade.activeItems[key].id === vocItemId,
                );
                if (typeOfActiveItem) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { [typeOfActiveItem]: _activeItem, ...hiddenActiveItem } = this.blade.activeItems;

                    let newActiveItem: any = {};
                    if (!isEmpty(hiddenActiveItem)) {
                        newActiveItem = {
                            ...values(hiddenActiveItem)[0],
                            isVisible: true,
                            ...(values(hiddenActiveItem)[0].associatedItemIds && {
                                associatedItemIds: values(hiddenActiveItem)[0].associatedItemIds.filter(
                                    (id: string) => id !== vocItemId,
                                ),
                            }),
                            ...(values(hiddenActiveItem)[0].associatedItemsExpanded && {
                                associatedItemsExpanded: values(hiddenActiveItem)[0].associatedItemsExpanded.filter(
                                    (expandedItem: any) => expandedItem.id !== vocItemId,
                                ),
                            }),
                        };

                        if (this.map[newActiveItem.id]) {
                            this.setMap({
                                map: {
                                    ...this.map,
                                    [newActiveItem.id]: newActiveItem,
                                },
                            });
                        } else if (getVocItemById(newActiveItem.id)) {
                            updateVocItemState(newActiveItem.id, newActiveItem);
                        }
                    }
                    cleanupVocItems();

                    setTimeout(() => {
                        this.setBlade({
                            activeItems: {
                                ...(!isEmpty(newActiveItem) && { [newActiveItem.type]: newActiveItem }),
                            },
                            openLinkedIdeasTab: !isEmpty(newActiveItem),
                        });
                    }, 0);

                    if (newActiveItem.type === 'feedback' && newActiveItem.numOfSuggestions > 0) {
                        this.setSuggestedItems({ item: newActiveItem });
                    }
                }
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (!isEmpty(this.allIdeas) && !reFetch) {
                return;
            }
            try {
                this.setFetching({ isFetching: true });
                const vocItems = await loadAllVocItems();
                this.setAllIdeas({ vocItems });
            } catch (error) {
                this.setError(error);
                throw error;
            } finally {
                this.setFetching({ isFetching: false });
            }
        },
        async addToAttachmentList ({ attachmentFile, type, id, vocItem, persist = false }: { attachmentFile: any; type: string; id: string; vocItem: any; persist?: boolean }) {
            try {
                this.pushToAttachmentList({
                    fileData: {
                        id,
                        type,
                        loading: true,
                    },
                });

                if (type === 'link' && persist) {
                    const linksData = this.attachmentList
                        .filter((link: any) => link.type === 'link' && !link.loading)
                        .map(({ file: link }: any) => {
                            return { linkUrl: link.name, title: link.title };
                        });

                    linksData.push({
                        linkUrl: attachmentFile.name,
                        title: attachmentFile.title,
                    });
                    const updatedVocItem = await updateVocItem(vocItem.id, { linksData });
                    this.updateBladeActiveVocItem({
                        changes: {
                            linksData,
                            lastUpdatedAt: updatedVocItem.lastUpdatedAt,
                            lastUpdatedBy: {
                                username: updatedVocItem.lastUpdatedBy.user?.username,
                            },
                        },
                    });
                }

                if (type === 'image' && persist) {
                    return ImageUploadQueue.addImage(async () => {
                        const createImageFile = await this.createAndAttachImage({
                            imageFile: attachmentFile,
                            vocItemId: vocItem.id,
                        });
                        const attachmentIds = [
                            ...(vocItem.attachmentIds?.length ? vocItem.attachmentIds : []),
                            createImageFile.id,
                        ];

                        this.updateBladeActiveVocItem({
                            changes: {
                                attachmentIds,
                                lastUpdatedAt: createImageFile.vocItem.lastUpdatedAt,
                                lastUpdatedBy: {
                                    username: createImageFile.vocItem.lastUpdatedBy.user?.username,
                                },
                            },
                        });

                        const newId = createImageFile.id;
                        const fileData = { file: attachmentFile, type, id, newId, loading: false };
                        this.updateAttachmentList({ fileData });
                    }).catch((error: any) => {
                        this.removeAttachmentFromList({ id });
                        throw error;
                    });
                }

                const fileData = { file: attachmentFile, type, id, loading: false };
                this.updateAttachmentList({ fileData });
            } catch (error) {
                this.removeAttachmentFromList({ id });
                throw error;
            }
        },
        async deleteAttachment ({ id, vocItem }: { id: string; vocItem: any }) {
            const fileData = this.attachmentList.find((attachment: any) => attachment.id === id);
            try {
                this.updateAttachmentList({
                    fileData: {
                        id,
                        loading: true,
                    },
                });
                await deleteAttachment(vocItem.id, id);
                this.removeAttachmentFromList({ id });

                this.updateBladeActiveVocItem({
                    changes: {
                        attachmentIds: vocItem.attachmentIds.filter((attachment: string) => attachment !== id),
                    },
                });
            } catch (error) {
                this.updateAttachmentList({ fileData });
                throw error;
            }
        },
        async deleteLink ({ id, vocItem }: { id: string; vocItem: any }) {
            const fileData = this.attachmentList.find((attachment: any) => attachment.id === id);

            try {
                this.updateAttachmentList({
                    fileData: {
                        id,
                        loading: true,
                    },
                });
                const linksData = this.attachmentList
                    .filter((link: any) => link.type === 'link' && link.id !== id)
                    .map(({ file: link }: any) => {
                        return { linkUrl: link.name, title: link.title };
                    });

                await updateVocItem(vocItem.id, { linksData });
                this.removeAttachmentFromList({ id });

                this.updateBladeActiveVocItem({
                    changes: {
                        linksData,
                    },
                });
            } catch (error) {
                this.updateAttachmentList({ fileData });
                throw error;
            }
        },
        async setSuggestedItems ({ item }: { item: any }) {
            this.setLoadingSuggestions({ loading: true });
            try {
                const subProductArea = item.type === 'idea' ? VALIDATE : CAPTURE;
                (VocItemSuggestionsForSingleItemAggregation as any).subProductArea = subProductArea;
                const paginateSuggestions = hasSegmentFlag('suggestedFeedbackImprovements') && item.type === 'idea';
                const limit = paginateSuggestions ? 25 : 10;

                const suggestionsPromise = runAggregationOnce(VocItemSuggestionsForSingleItemAggregation, {
                    vocItemId: item.id,
                    type: item.type,
                    limit,
                }) as Promise<any[]>;

                let countPromise: Promise<{ total?: number }> | null = null;

                if (paginateSuggestions) {
                    (VocItemSuggestionsCountForSingleItemAggregation as any).subProductArea = subProductArea;
                    countPromise = runAggregationOnce(VocItemSuggestionsCountForSingleItemAggregation, {
                        vocItemId: item.id,
                        type: item.type,
                    }) as Promise<{ total?: number }>;
                }

                const [suggestionsResult, countResult] = await Promise.allSettled([suggestionsPromise, countPromise]);

                if (suggestionsResult.status === 'rejected') throw suggestionsResult.reason;
                if (countResult.status === 'rejected') throw countResult.reason;

                const suggestions = suggestionsResult.value;
                const count = paginateSuggestions ? countResult.value?.total ?? 0 : suggestions.length;
                this.setActiveSuggestions({ suggestions });
                this.setSuggestionsTotal({ total: count });

                if (suggestions.length === 0) {
                    this.setLoadingSuggestions({ loading: false });
                }
            } catch (error) {
                this.setLoadingSuggestions({ loading: false });
                this.setError(error);
                throw error;
            }
        },
        async rejectSuggestion ({ suggestionId }: { suggestionId: string }) {
            try {
                await rejectSuggestion({ suggestionId });
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async loadVocItemById ({ vocItemId }: { vocItemId: string } = { vocItemId: '' }) {
            try {
                this.setFetching({ isFetching: true });
                const vocItem = await fetchVocItemData(vocItemId);
                this.setMap({
                    map: {
                        ...this.map,
                        [vocItem.id]: vocItem,
                    },
                });
            } catch (error) {
                this.setError(error);
                throw error;
            } finally {
                this.setFetching({ isFetching: false });
            }
        },
        removeSuggestion ({ itemId }: { itemId: string }) {
            this.setActiveSuggestions({
                suggestions: this.blade.activeItemSuggestedItems.filter((suggestion: any) => suggestion.leftId !== itemId),
            });
        },
        async linkFeedbackToIdea (
            { feedbackId, ideaId, suggested = false, context = 'unknown' }: { feedbackId: string; ideaId: string; suggested?: boolean; context?: string },
        ) {
            try {
                await linkFeedbackToIdea({ feedbackId, ideaId });

                window.pendo.track('feedback-linked-to-idea', {
                    suggested,
                    context,
                });
                if (!suggested || context !== 'feedback-blade') {
                    const currentItem = this.map[this.bladeActiveItem?.id] || this.bladeActiveItem;
                    const revenueField = get(useSubscriptionStore().settings, 'revenue.value', null);
                    const accountDisplayName = get(useSubscriptionStore().settings, 'accountDisplayName.value', null);
                    (VocItemAggregation as any).subProductArea = currentItem.type === 'idea' ? VALIDATE : CAPTURE;
                    const updatedItem = await runAggregationOnce(VocItemAggregation, {
                        vocItemId: this.bladeActiveItem?.id,
                        appId: this.appIdsForAggregation(currentItem),
                        type: currentItem.type,
                        segmentId: currentItem.type === 'idea' ? useGlobalFiltersStore().selectedSegmentId : null,
                        allTime: currentItem.type === 'idea' ? useGlobalFiltersStore().allTime : true,
                        timeSeriesConfig:
                            currentItem.type === 'idea' ? useGlobalFiltersStore().timeSeriesSpec : null,
                        shouldFilterVotes: false,
                        revenueField,
                        accountDisplayName,
                    }) as any;
                    const formattedItem = {
                        ...currentItem,
                        ...updatedItem,
                        associatedItemsExpanded:
                            updatedItem.associatedItemsExpanded || currentItem.associatedItemsExpanded,
                        isVisible: true,
                    };
                    if (this.map[this.bladeActiveItem?.id]) {
                        this.setMap({
                            map: {
                                ...this.map,
                                [updatedItem.id]: formattedItem,
                            },
                        });
                    } else {
                        const { getVocItemById, updateVocItem: updateVocItemState, cleanupVocItems } = useVocItems({
                            type: currentItem.type,
                        });
                        if (getVocItemById(this.bladeActiveItem?.id)) {
                            updateVocItemState(this.bladeActiveItem.id, formattedItem);
                        }
                        cleanupVocItems();
                    }
                    if (this.blade.activeItems[formattedItem.type]?.id === formattedItem.id) {
                        this.setBladeActiveItems({
                            activeItems: {
                                ...this.blade.activeItems,
                                [formattedItem.type]: formattedItem,
                            },
                        });
                    }
                    if (this.blade.openingOnTopOfStackedBlade) EventBus.$emit('update-stacked-blade', formattedItem);
                }
                this.incrementBladeChangeTrigger();
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async unlinkFeedbackFromIdea (
            { feedbackId, ideaId, vocItem, source }: { feedbackId: string; ideaId: string; vocItem?: any; source?: string },
        ) {
            try {
                if (source === 'Insight') {
                    await unlinkInsightFromIdea({ feedbackId, ideaId });
                } else {
                    await unlinkFeedbackFromIdea({ feedbackId, ideaId });
                }

                const currentItem = vocItem || this.map[this.bladeActiveItem?.id] || this.bladeActiveItem;
                const revenueField = get(useSubscriptionStore().settings, 'revenue.value', null);
                const accountDisplayName = get(useSubscriptionStore().settings, 'accountDisplayName.value', null);
                (VocItemAggregation as any).subProductArea = currentItem.type === 'idea' ? VALIDATE : CAPTURE;
                const updatedItem = await runAggregationOnce(VocItemAggregation, {
                    vocItemId: currentItem?.id,
                    appId: this.appIdsForAggregation(currentItem),
                    type: currentItem.type,
                    segmentId: currentItem.type === 'idea' ? useGlobalFiltersStore().selectedSegmentId : null,
                    allTime: currentItem.type === 'idea' ? useGlobalFiltersStore().allTime : true,
                    timeSeriesConfig: currentItem.type === 'idea' ? useGlobalFiltersStore().timeSeriesSpec : null,
                    shouldFilterVotes: false,
                    revenueField,
                    accountDisplayName,
                }) as any;
                if (this.map[currentItem?.id]) {
                    this.setMap({
                        map: {
                            ...this.map,
                            [updatedItem.id]: {
                                ...currentItem,
                                ...updatedItem,
                                associatedItemsExpanded:
                                    updatedItem.associatedItemsExpanded || currentItem.associatedItemsExpanded,
                            },
                        },
                    });
                } else {
                    const { getVocItemById, updateVocItem: updateVocItemState, cleanupVocItems } = useVocItems({ type: currentItem.type });
                    if (getVocItemById(this.bladeActiveItem?.id)) {
                        updateVocItemState(updatedItem.id, {
                            ...currentItem,
                            ...updatedItem,
                            associatedItemsExpanded:
                                updatedItem.associatedItemsExpanded || currentItem.associatedItemsExpanded,
                        });
                    }
                    cleanupVocItems();
                }
                if (this.blade.activeItems[updatedItem.type]?.id === updatedItem.id) {
                    this.setBladeActiveItems({
                        activeItems: {
                            ...this.blade.activeItems,
                            [updatedItem.type]: {
                                ...this.blade.activeItems[updatedItem.type],
                                ...updatedItem,
                                associatedItemsExpanded:
                                    updatedItem.associatedItemsExpanded || currentItem.associatedItemsExpanded,
                            },
                        },
                    });
                }

                this.incrementBladeChangeTrigger();
                if (this.blade.openingOnTopOfStackedBlade) {
                    EventBus.$emit('update-stacked-blade', {
                        ...currentItem,
                        ...updatedItem,
                    });
                }
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        setVocItems ({ vocItems, type = 'idea' }: { vocItems: any[]; type?: string }) {
            const storedVocItems = values(this.map).filter((item: any) => item.type !== type);
            const newVocItems = [...storedVocItems, ...vocItems];

            this.setMap({ map: keyBy(newVocItems, 'id') });

            const activeItems = values(this.blade.activeItems);
            if (activeItems.length > 0) {
                const updatedActiveItems = activeItems.map((item: any) => {
                    const matchingVocItem = vocItems.find((vocItem: any) => vocItem.id === item.id);
                    if (matchingVocItem) {
                        return {
                            ...item,
                            ...matchingVocItem,
                            associatedItemsExpanded: item.associatedItemsExpanded?.filter((expandedItem: any) => {
                                return matchingVocItem.associatedItemIds?.includes(expandedItem.id);
                            }),
                        };
                    }

                    return item;
                });

                this.setBladeActiveItems({
                    activeItems: keyBy(updatedActiveItems, 'type'),
                });
            }
        },
        updateVocItemInMap ({ vocItem }: { vocItem: any }) {
            if (this.map[vocItem.id]) {
                this.setMap({ map: { ...this.map, [vocItem.id]: vocItem } });
            } else {
                const { getVocItemById, updateVocItem: updateVocItemState, cleanupVocItems } = useVocItems({ type: vocItem.type });
                if (getVocItemById(vocItem.id)) {
                    updateVocItemState(vocItem.id, vocItem);
                }
                cleanupVocItems();
            }
            this.updateVisitorVotedIdeas({ vocItem });
        },
        updateSingleItemLinkedItems ({ id, linkedItem, lastUpdatedAt }: { id: string; linkedItem: any; lastUpdatedAt: any }) {
            if (this.bladeHiddenActiveItem?.id === id) {
                this.updateBladeHiddenVocItemLinkedItems({ linkedItem, lastUpdatedAt });
            }
            if (this.map[id]) {
                this.updateLinkedItemsInMap({ id, linkedItem, lastUpdatedAt });
            }
            this.incrementBladeChangeTrigger();
        },
        async uploadCSV ({ file }: { file: any }) {
            try {
                const bucketResp = await createCSVBucket();
                const assignedFileName = bucketResp.url.split('/').pop();
                await gcsPost(bucketResp.uploadUrl, file, bucketResp.formData);
                const uploadResp = await processCSV({ name: assignedFileName });

                return {
                    data: uploadResp.rows,
                    assignedFileName,
                };
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async bulkLinkFeedbackToIdea ({ feedbackItems, insightIds, idea }: { feedbackItems: any[]; insightIds: string[]; idea: any }) {
            try {
                const bulkLinkResponse = await bulkLinkFeedbackToIdea({
                    ideaId: idea.id,
                    feedbackIds: feedbackItems.map((feedbackItem: any) => feedbackItem.id),
                    insightIds,
                });
                if (feedbackItems.length && bulkLinkResponse.feedback.FailedCount === feedbackItems.length) {
                    throw new Error('Failed to link any feedback items');
                }
                if (insightIds.length && bulkLinkResponse.insights.FailedCount === insightIds.length) {
                    throw new Error('Failed to link any insights');
                }

                const mapUpdates: Record<string, any> = {};

                feedbackItems
                    .filter(
                        (feedbackItem: any) =>
                            (!Array.isArray(feedbackItem.associatedItemIds) ||
                                !feedbackItem.associatedItemIds.includes(idea.id)) &&
                            !bulkLinkResponse.FailedChildren?.includes(feedbackItem.id),
                    )
                    .forEach((feedbackItem: any) => {
                        mapUpdates[feedbackItem.id] = {
                            ...feedbackItem,
                            associatedItemIds: feedbackItem.associatedItemIds
                                ? [...feedbackItem.associatedItemIds, idea.id]
                                : [idea.id],
                            associatedItemsExpanded: feedbackItem.associatedItemsExpanded
                                ? [...feedbackItem.associatedItemsExpanded, idea]
                                : [idea],
                        };
                    });
                if (getExploreStatus() !== EXPLORE_STATUSES.READY) {
                    this.setMap({ map: { ...this.map, ...mapUpdates } });
                } else {
                    const { addVocItems: updateVocItems, cleanupVocItems } = useVocItems({ type: 'feedback' });
                    updateVocItems(Object.values(mapUpdates));

                    cleanupVocItems();
                }

                return bulkLinkResponse;
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async getVocItemUpdateEmail ({ vocItemId, allUpdates = false }: { vocItemId: string; allUpdates?: boolean }) {
            try {
                let vocItemUpdateEmail: any;
                let updateLog: any;

                if (this.shareUpdateV2Visible) {
                    updateLog = await getVocItemEmailUpdates(vocItemId);

                    vocItemUpdateEmail = updateLog
                        .filter((update: any) => update.campaignStatus === 'sent' || update.campaignStatus === 'public')
                        .sort((a: any, b: any) => b.firstSent - a.firstSent)[0];
                } else {
                    vocItemUpdateEmail = await getUpdateEmail(vocItemId);
                }

                this.setLastVocItemUpdateEmail(vocItemUpdateEmail);

                return allUpdates ? updateLog : vocItemUpdateEmail;
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async getVocItemListWithSourceIdentifier ({ source }: { source: string }) {
            const rows = await runAggregationOnce(VocItemListWithSourceIdentifierAggregation, { type: 'feedback', source });
            (rows as any[]).forEach((pair: any) => {
                this.addResponseToResponsesAlreadySentToFeedback({
                    sourceIdentifier: pair.pollResponseId,
                    vocItemId: pair.vocItemId,
                });
            });
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useVocItemsStore, module.hot));
}
